import React, { useEffect, useCallback, useState } from "react";
import { Shield, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

// Client-side preview of the same safety patterns used by the backend.
// Backend remains the source of truth (re-validates + stores flag_reasons).
const UNSAFE_PATTERNS = [
  /\b(\+?\d[\d\-\s().]{7,}\d)\b/,                            // phone-ish
  /\b[\w.+-]+@[\w-]+\.[\w.-]+\b/,                            // email
  /\b(venmo|zelle|cashapp|paypal\.me|wire|bitcoin|btc|eth|crypto|whatsapp|telegram|signal|wechat|imessage)\b/i,
  /https?:\/\/|www\./i,
];
const hasUnsafe = (txt) => UNSAFE_PATTERNS.some(re => re.test(txt || ""));

export default function Messages(){
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const [warn, setWarn] = useState(false);
  const [sendErr, setSendErr] = useState("");

  const load = useCallback(() => {
    api.get("/messages").then(r=>{
      const data = Array.isArray(r.data) ? r.data : [];
      setThreads(data);
      setActive(prev => prev || data[0] || null);
    }).catch(()=>setThreads([]));
  }, []);
  useEffect(()=>{ load(); },[load]);

  useEffect(() => { setWarn(hasUnsafe(text)); }, [text]);

  const send = async () => {
    setSendErr("");
    if (!text || !active?.with_user?.user_id) return;
    try {
      await api.post("/messages", { to_user: active.with_user.user_id, text });
      setText(""); load();
    } catch (e) {
      setSendErr(e?.response?.data?.detail || "Could not send message.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 pb-28 lg:pb-10 grid md:grid-cols-3 gap-6 h-[80vh]">
      <div className="rounded-3xl bg-[#FFF8EE] border p-4 overflow-auto">
        <h2 className="font-serif text-xl mb-2">Conversations</h2>
        <div className="text-[11px] text-[#071A2D]/55 mb-3 flex items-start gap-1.5">
          <Shield size={11} className="mt-0.5 shrink-0 text-[#064E3B]"/>
          <span>Messaging unlocks after a booking request.</span>
        </div>
        {threads.length===0 && <div className="text-sm text-[#071A2D]/50">Your conversations will appear here once you request an experience.</div>}
        {threads.map(t=>(
          <button key={t.with_user.user_id} onClick={()=>{setActive(t); setSendErr("");}} className={`w-full text-left p-3 rounded-2xl mb-1 ${active?.with_user.user_id===t.with_user.user_id?"bg-[#064E3B] text-[#FFF8EE]":"hover:bg-white/40"}`}>
            <div className="font-medium">{t.with_user.name}</div>
            <div className="text-xs opacity-70 truncate">{t.last.text}</div>
          </button>
        ))}
      </div>
      <div className="md:col-span-2 rounded-3xl bg-[#FFF8EE] border flex flex-col">
        <div className="flex-1 p-6 overflow-auto">
          {!active && <div className="text-center text-[#071A2D]/40 mt-20">Select a conversation</div>}
          {active?.messages?.map(m=>(
            <div key={m.message_id} className={`mb-3 flex ${m.to_user===active?.with_user?.user_id?"justify-end":"justify-start"}`}>
              <div className={`max-w-sm p-3 rounded-2xl ${m.to_user===active?.with_user?.user_id?"bg-[#064E3B] text-[#FFF8EE]":"bg-white border"} ${m.flagged?"border-[#FF7A45]":""}`}>
                {m.text}
                {m.flagged && (
                  <div className="mt-2 text-[10px] uppercase tracking-widest opacity-80 flex items-center gap-1">
                    <AlertTriangle size={10}/> Flagged · review
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {active && (
          <div className="border-t">
            {/* Safety reminder — always visible inside open threads */}
            <div className="px-4 pt-3 text-[11px] text-[#071A2D]/60 flex items-start gap-1.5">
              <Shield size={11} className="mt-0.5 shrink-0 text-[#064E3B]"/>
              <span>For your safety, keep communication and payments inside TREL.</span>
            </div>
            {warn && (
              <div data-testid="msg-warning" className="mx-4 mt-2 p-3 rounded-2xl bg-[#FF7A45]/10 border border-[#FF7A45]/30 text-[12px] text-[#FF7A45] flex items-start gap-2">
                <AlertTriangle size={13} className="mt-0.5 shrink-0"/>
                <span>Sharing phone numbers, emails, external payment apps, or links may put you outside TREL&apos;s protections. Please keep coordination on-platform.</span>
              </div>
            )}
            {sendErr && (
              <div data-testid="msg-error" className="mx-4 mt-2 p-3 rounded-2xl bg-[#FF7A45]/10 border border-[#FF7A45]/30 text-[12px] text-[#FF7A45]">{sendErr}</div>
            )}
            <div className="p-4 flex gap-3">
              <input data-testid="msg-input" value={text} onChange={e=>setText(e.target.value)} placeholder="Message…" className="kbd-input flex-1"/>
              <button onClick={send} className="btn-coral" data-testid="msg-send">Send</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
